import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Crown, LogOut, Settings, User } from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import BrandLogo from "@/components/BrandLogo";
import ProfileAccountDialog from "@/components/ProfileAccountDialog";
import TopBarSearch from "@/components/TopBarSearch";
import TopBarNotifications from "@/components/TopBarNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { canAccess } from "@/lib/permissions";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AppTopBar({ onLogout }) {
    const navigate = useNavigate();
    const { user, setUser } = useAuth();
    const [profileOpen, setProfileOpen] = useState(false);

    const showSubscription = canAccess(user?.role, "billing");

    return (
        <>
            <header
                className="shrink-0 border-b border-[#E8E4DE] bg-gradient-to-r from-white via-[#FFFEF9] to-white px-5 shadow-[0_1px_0_rgba(212,175,55,0.12)] sm:px-6"
                data-testid="app-topbar"
            >
                <div className="mx-auto grid h-[60px] max-w-[1600px] grid-cols-[1fr_auto_1fr] items-center gap-3">
                    <div className="min-w-0 justify-self-start">
                        <BrandLogo
                            size={30}
                            textClass="font-serif text-[1.35rem] font-semibold leading-none text-[#4A0E17]"
                            accentClass="text-[11px] uppercase tracking-[0.18em] text-[#C9A227] font-semibold"
                        />
                        {user?.tenant_name && (
                            <p
                                className="mt-0.5 max-w-[200px] truncate text-[11px] font-semibold text-[#8A8178] sm:max-w-[280px]"
                                data-testid="topbar-salon-name"
                            >
                                {user.tenant_name}
                            </p>
                        )}
                    </div>

                    <div className="w-full min-w-[200px] max-w-[300px] justify-self-center">
                        <TopBarSearch role={user?.role} />
                    </div>

                    <div className="flex min-w-0 items-center justify-end gap-1 justify-self-end sm:gap-2">
                        {showSubscription && (
                            <button
                                type="button"
                                onClick={() => navigate("billing")}
                                className="group relative flex size-9 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-gradient-to-br from-[#FFF8E7] via-[#FFFBF5] to-white text-[#C9A227] shadow-[0_2px_8px_rgba(201,162,39,0.18)] transition-all hover:border-[#C9A227]/65 hover:shadow-[0_4px_14px_rgba(201,162,39,0.28)] hover:text-[#9A7B10]"
                                aria-label="Subscription"
                                title="Subscription"
                                data-testid="topbar-subscription"
                            >
                                <span
                                    className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.22),transparent_55%)] opacity-0 transition-opacity group-hover:opacity-100"
                                    aria-hidden
                                />
                                <Crown className="relative size-[17px]" strokeWidth={2.35} />
                            </button>
                        )}
                        <TopBarNotifications role={user?.role} />

                        <div className="mx-0.5 hidden h-7 w-px bg-[#E8E4DE] sm:block" aria-hidden />

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className="flex max-w-[200px] items-center gap-2 rounded-full border border-[#E8E4DE] bg-white py-1 pl-1 pr-2.5 shadow-sm transition-all hover:border-[#D4AF37]/40 hover:shadow-md sm:max-w-none sm:pr-3"
                                    data-testid="topbar-profile-menu"
                                >
                                    <UserAvatar user={user} size="sm" />
                                    <span className="hidden min-w-0 text-left md:block">
                                        <span className="block truncate text-sm font-semibold leading-tight text-[#2D2926]">
                                            {user?.full_name || "User"}
                                        </span>
                                        <span className="block truncate text-[11px] capitalize text-[#8A8178]">
                                            {user?.role || "staff"}
                                        </span>
                                    </span>
                                    <ChevronDown className="size-3.5 shrink-0 text-[#8A8178]" strokeWidth={2.5} />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="w-56 rounded-xl border-[#E8E4DE] p-1.5 font-['Outfit',system-ui,sans-serif] shadow-[0_12px_40px_rgba(45,41,38,0.1)]"
                            >
                                <DropdownMenuLabel className="px-2 py-2 font-normal">
                                    <p className="truncate text-sm font-semibold text-[#2D2926]">
                                        {user?.full_name}
                                    </p>
                                    <p className="truncate text-xs text-[#8A8178]">{user?.email}</p>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-[#E8E4DE]" />
                                <DropdownMenuItem
                                    className="cursor-pointer gap-2.5 rounded-lg px-2 py-2 text-sm text-[#2D2926] focus:bg-[#FAF9F6] focus:text-[#4A0E17]"
                                    onSelect={() => setProfileOpen(true)}
                                    data-testid="topbar-profile"
                                >
                                    <User className="size-4 text-[#8A8178]" />
                                    My profile
                                </DropdownMenuItem>
                                {user?.role === "owner" && (
                                    <DropdownMenuItem
                                        className="cursor-pointer gap-2.5 rounded-lg px-2 py-2 text-sm text-[#2D2926] focus:bg-[#FAF9F6] focus:text-[#4A0E17]"
                                        onSelect={() => navigate("settings")}
                                        data-testid="topbar-settings"
                                    >
                                        <Settings className="size-4 text-[#8A8178]" />
                                        Salon settings
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-[#E8E4DE]" />
                                <DropdownMenuItem
                                    className="cursor-pointer gap-2.5 rounded-lg px-2 py-2 text-sm text-[#C84B31] focus:bg-[#FEF5F3] focus:text-[#C84B31]"
                                    onSelect={onLogout}
                                    data-testid="topbar-signout"
                                >
                                    <LogOut className="size-4" />
                                    Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <ProfileAccountDialog
                open={profileOpen}
                onOpenChange={setProfileOpen}
                user={user}
                onUserUpdated={setUser}
            />
        </>
    );
}
